/**
 * One-machine intents: history, why it failed, likely causes, telemetry, what
 * to change next (and what it costs), and the bare "tell me about L-02".
 *
 * Every intent here `needs: ['machine']` — the router fills a missing machine
 * from the page (`/machines/T-09`, an alert or incident page) or from the
 * conversation, and the answer says where it came from.
 */

import { BY_ID, MINES } from '../../../data/core.ts';
import { CONDITION_BAND_LABEL } from '../../../schema/machine.ts';
import { causesFor, groupNum, healthBand, healthOf, INC_PLANNED, incidentCosts, fmt$, intervalOf, wosOf } from '../../oem-engine.ts';
import { INCIDENTS } from '../../seed/incidents.ts';
import { money } from '../stats.ts';
import { clause, route, scopeLabel } from '../answer-kit.ts';
import { TOOLS, loadWorkOrders } from '../tools.ts';
import { BREAK_DOWN_AS_VERB } from './fleet.ts';
import { cues, type AnswerCtx, type IntentDef } from './types.ts';
import type { MineCode } from '../../../schema/machine.ts';

/** `L-02 (this page)` — the machine, with where it came from. */
const named = (ctx: AnswerCtx): string => {
  const s = scopeLabel(ctx.E);
  return s ? s.slice(3) : (ctx.E.machine as string);
};

// ---------------------------------------------------------------------------
// machine_history — demo L4072
// ---------------------------------------------------------------------------

export async function machineHistoryAnswer(ctx: AnswerCtx) {
  const { kit, E } = ctx;
  const mid = E.machine as string;
  const h = await kit.run(TOOLS.machine_history, { machine_id: mid });
  const bd = h.recent_work.filter((w) => w.type === 'Breakdown');
  const flagged = h.flagged_components.length
    ? `- Flagged: ${h.flagged_components
        // demo bug: the original read `c.health`, which the tool never emits.
        .map((c) => `**${c.component}** (condition ${c.condition}/100) — ${c.note}`)
        .join('; ')}\n`
    : '';
  const recent = bd.length
    ? `- Recent breakdowns: ${bd
        .slice(0, 4)
        .map(
          (w) =>
            `${w.date} ${w.component} — ${w.root_cause || w.desc} (${w.downtime_h} h, ${money(w.cost)}, ${w.crew})`,
        )
        .join('; ')}\n`
    : '';
  const heading = E.machineFrom && E.machineFrom !== 'text' ? named(ctx) : mid;
  const markdown =
    `**${heading} · ${h.class_name} · ${h.mine}** — ${groupNum(h.engine_hours)} engine h, condition ${h.status}.\n\n` +
    `- ${h.breakdowns} breakdowns and ${h.correctives} correctives in 18 months; ${h.pms_late} of ${h.pms} PMs ran late.\n` +
    `- All-in cost ${money(h.total_cost_18m)} → **A$${h.cost_per_op_hour}/op h** vs A$${h.model_avg_cost_per_op_hour} for the ${h.model} fleet.\n` +
    flagged +
    recent +
    (h.alerts.length ? `- Open alert: ${h.alerts.join('; ')}\n` : '') +
    (h.cases.length ? `- Cases: ${h.cases.join('; ')}` : '');
  kit.focus({ machine: mid, mine: h.mine_code, component: h.lowest_component.component });
  return kit.done(markdown, ['machine_history', 'SAP PM work_orders', mid]);
}

export const machineHistory: IntentDef = {
  id: 'machine_history',
  family: 'machine',
  priority: 68,
  examples: ['Give me the full history of L-02', 'Why does T-09 keep breaking down?'],
  needs: ['machine'],
  score: cues({
    any: [
      /histor/,
      /breakdown/,
      /\bkeep\b/,
      /track record/,
      /cost(ed)? per (operating|op) ?h(ou)?r?|per op h|what has (it|this machine) cost/,
      /tell me (about|everything)|full (story|picture|record)/,
    ],
    not: [BREAK_DOWN_AS_VERB, /how many .*(failures?|breakdowns?)/],
  }),
  answer: machineHistoryAnswer,
};

// ---------------------------------------------------------------------------
// machine_lookup — a bare machine mention; the lowest-scoring intent there is
// ---------------------------------------------------------------------------

export const machineLookup: IntentDef = {
  id: 'machine_lookup',
  family: 'machine',
  priority: 1,
  examples: ['Tell me about L-02'],
  score: (_L, E) => (E.machineFrom === 'text' ? 1.5 : 0),
  answer: machineHistoryAnswer,
};

// ---------------------------------------------------------------------------
// why_failed — new: "why did L-02 fail?"
// ---------------------------------------------------------------------------

export const whyFailed: IntentDef = {
  id: 'why_failed',
  family: 'machine',
  priority: 76,
  examples: ['Why did L-02 fail?', 'What caused the S-05 incident?'],
  needs: ['machine'],
  score: cues({
    any: [
      /why did .*(fail|break|go down|stop|shear|catch fire|burn|part)/,
      /why .*(failed|broke|sheared|went down|stopped)/,
      /what caused|root cause|cause of the .*(failure|incident|fire|breakdown)/,
      /what went wrong (with|on)/,
    ],
    base: 6,
  }),
  async answer(ctx) {
    const { kit, E } = ctx;
    const mid = E.machine as string;
    const inc = INCIDENTS.find((i) => i.machine === mid);
    const workOrders = await loadWorkOrders();
    kit.focus({ machine: mid, mine: BY_ID[mid].mine });
    if (inc) {
      kit.ran('incident_costs', { incident: inc.id });
      const c = incidentCosts(inc, { workOrders });
      const w = c.workOrder;
      kit.focus({ component: inc.comp });
      const precursors = inc.happened
        .slice(0, 3)
        .map((h) => `**${h.when}** — ${h.text.split('. ')[0]}.`)
        .join(' ');
      const planned = INC_PLANNED[inc.id];
      const markdown =
        `**${named(ctx)} · ${inc.title}** — ${MINES[inc.mine as MineCode].name}, ${inc.date}.\n\n` +
        `Root cause on the work order: **${clause(w?.root_cause ?? w?.failure_cause ?? inc.comp)}**.\n\n` +
        `How it got there: ${precursors}\n\n` +
        `It cost ${fmt$(c.allIn ?? 0)} all-in against ${fmt$(planned)} for the planned job. ` +
        `Open the incident to replay the fortnight with the precursor rule switched on.`;
      return kit.done(markdown, ['incidents', 'work_orders.csv', inc.workOrder], {
        navigate: route('incident', inc.id),
      });
    }
    kit.ran('breakdown_causes', { machine_id: mid });
    const bd = wosOf(mid, { workOrders }).filter((w) => w.wo_type === 'Breakdown');
    const last = bd[0];
    if (!last) {
      const worst = healthOf(mid).slice().sort((a, b) => a.health_score - b.health_score)[0];
      const markdown =
        `**${named(ctx)}** has no breakdown on record in the 18-month ledger. ` +
        `Its weakest component is **${worst.component}** at ${worst.health_score}/100 (${CONDITION_BAND_LABEL[healthBand(worst)]}), ` +
        `${groupNum(worst.hours_since_change)} h since change.`;
      return kit.done(markdown, ['SAP PM work_orders', 'component_health']);
    }
    kit.focus({ component: last.component });
    const causes = causesFor(last.component, { workOrders });
    const markdown =
      `**${named(ctx)}** last broke down on ${last.created.slice(0, 10)}: **${last.component}** — ` +
      `${clause(last.root_cause || last.description.split('. ')[0])} (${last.downtime_h ?? '—'} h down, ${money(
        last.total_direct_cost_aud + (last.downtime_cost_aud || 0),
      )} all-in, ${last.crew}).\n\n` +
      `${bd.length} breakdown${bd.length === 1 ? '' : 's'} in 18 months. ` +
      `Across the fleet, ${last.component} failures rank: ${causes
        .slice(0, 4)
        .map((x) => `**${x.cause}** ${x.pct}%`)
        .join(', ')}.`;
    return kit.done(markdown, ['work_orders.csv', 'cause ranking', mid]);
  },
};

// ---------------------------------------------------------------------------
// likely_causes — demo L4059
// ---------------------------------------------------------------------------

export const likelyCauses: IntentDef = {
  id: 'likely_causes',
  family: 'machine',
  priority: 94,
  examples: ['What are the likely causes of the L-20 turbo failure?'],
  needs: ['machine'],
  score: cues({ any: [/likely causes?|possible causes?|probable causes?/], base: 5 }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const workOrders = await loadWorkOrders();
    const draft = workOrders.filter((x) => x.machine_id === mid && x.status.startsWith('Draft'))[0];
    const fallback = wosOf(mid, { workOrders }).find((x) => x.wo_type !== 'Preventive');
    const w = draft ?? fallback;
    const comp =
      w?.component ??
      healthOf(mid)
        .slice()
        .sort((a, b) => a.health_score - b.health_score)[0].component;
    kit.ran('cause_ranking', { component: comp, machine_id: mid });
    const c = causesFor(comp, { workOrders });
    const n = workOrders.filter((x) => x.component === comp && x.wo_type !== 'Preventive').length;
    const markdown =
      `For **${comp}** on ${mid}, the fleet's ${n} corrective work orders on that component rank the causes: ` +
      `${c.map((x) => `**${x.cause}** ${x.pct}%`).join(', ')}. ` +
      `The job card carries the tools, the quote and the controlled procedure — open it from Job cards.`;
    kit.focus({ machine: mid, component: comp, mine: BY_ID[mid].mine });
    return kit.done(markdown, ['work_orders.csv', 'job cards'], { navigate: route('jobcards') });
  },
};

// ---------------------------------------------------------------------------
// telemetry — demo L4078 (was: any question naming a machine)
// ---------------------------------------------------------------------------

export const telemetry: IntentDef = {
  id: 'telemetry',
  family: 'machine',
  priority: 40,
  examples: ["Is T-09's telemetry drifting?", 'What are the alarm codes on L-13?'],
  needs: ['machine'],
  score: cues({
    any: [/telemetry|drift|alarms?\b|sensor|signal|temperatures?|pressures?|readings?|live feed|fault codes?/],
    penalty: [[/\bstock|only one|network|in store/, 4]],
  }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const t = await kit.run(TOOLS.fault_trend, { machine_id: mid });
    const codes = Object.entries(t.alarm_codes)
      .map(([k, v]) => `${k} ×${v}`)
      .join(', ');
    const label = E.machineFrom && E.machineFrom !== 'text' ? scopeLabel(E).slice(3) : mid;
    const markdown =
      `**${label} telemetry** (${t.data_mode}) — ${String(t.metric).replace(/_/g, ' ')} moved ` +
      `${t.change_over_window > 0 ? '+' : ''}${t.change_over_window} over 12 days ` +
      `(slope ${t.slope_per_day}/day${t.limit ? `, limit ${t.limit}` : ''}). ` +
      `Alarm codes: ${codes || 'none'}. ` +
      `${t.live_sample ? `Live shift sample: ${t.live_sample.exceedances} exceedances, max ${t.live_sample.max_brake_temp} °C.` : ''}`;
    kit.focus({ machine: mid, mine: BY_ID[mid].mine });
    return kit.done(markdown.trim(), ['fault_trend', 'telemetry']);
  },
};

// ---------------------------------------------------------------------------
// change_next — new: "what should we change next on L-02 and what will it cost?"
// ---------------------------------------------------------------------------

export const changeNext: IntentDef = {
  id: 'change_next',
  family: 'machine',
  priority: 78,
  examples: ['What should we change next on L-02 and what will it cost?', 'Which component should we change next on T-09?'],
  needs: ['machine'],
  score: cues({
    any: [
      /change next|replace next|swap next|next (change|change-out|replacement)/,
      /(what|which component) should (we|i) (change|replace)/,
      /which component .*(change|replace)/,
    ],
    base: 6,
  }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const health = healthOf(mid).slice().sort((a, b) => a.health_score - b.health_score);
    kit.ran('component_health', { machine_id: mid });
    const worst = health[0];
    const j = await kit.run(TOOLS.job_cost, { machine_id: mid, component: worst.component });
    const f = BY_ID[mid];
    const interval = intervalOf(worst.component);
    const next = health.slice(1, 3).map((h) => `${h.component} ${h.health_score}/100`).join(', ');
    const basis =
      j.basis === 'alert'
        ? `priced on the open alert ${j.alert} (${j.plan_note})`
        : j.basis === 'draft'
          ? `the agent-drafted ${j.draft?.wo} (${j.draft?.labour_h} h, ${j.draft?.crew})`
          : `estimated: catalogue parts plus the fleet's mean labour hours on ${worst.component} at A$${j.labour_rate}/h`;
    const markdown =
      `**Change next on ${E.machineFrom && E.machineFrom !== 'text' ? scopeLabel(E).slice(3) : mid}: ${worst.component}** — condition ${worst.health_score}/100 ` +
      `(${CONDITION_BAND_LABEL[healthBand(worst)]}), ${groupNum(worst.hours_since_change)} h since change against a ` +
      `${groupNum(interval)} h interval${worst.note ? `; ${worst.note.replace(/[.\s]+$/, '')}` : ''}.\n\n` +
      `- **Planned cost ${fmt$(j.planned)}** — ${basis}.\n` +
      (j.unplanned != null
        ? `- If it fails first: ${fmt$(j.unplanned)} (${j.downtime_h_unplanned} h down at A$${groupNum(j.lost_rate)}/h).\n`
        : `- Unplanned, the same job adds downtime at A$${groupNum(j.lost_rate)}/h for the ${f.model} at ${MINES[f.mine].name}.\n`) +
      (j.parts.length
        ? `- Parts: ${j.parts.map((p) => `${p.part} ${p.description} A$${groupNum(p.list_price_aud)} (${p.lead_time_days} d)`).join('; ')}.\n`
        : '') +
      (next ? `\nAfter that: ${next}.` : '');
    kit.focus({ machine: mid, component: worst.component, mine: f.mine });
    kit.remember({ component: worst.component });
    return kit.done(markdown.trim(), ['component_health', 'job_cost', 'rate card', 'parts_catalogue.csv']);
  },
};
